/**
 * TabAdvert component displays a sticky advertisement banner at the bottom of the screen.
 * It handles displaying ads in a tab layout, tracking ad impressions, and managing ad visibility.
 */
import { useEffect, useMemo, useState } from "react";
import { Animated, Platform } from "react-native";
import variables from "../../styles/variables";
import AdBanner from "./AdBanners/AdBanner";
import { useAtom, useSetAtom } from "jotai";
import {
  appHeightAndWidthAtom,
  advertisingAtom,
  tabAdLayoutAtom,
} from "../../atoms";
import { useShowTabAd } from "../../hooks/useShowTabAd";
import { logEvent } from "../../client/analytics/trackPageView";
import { advertClick } from "../../client/adverts";
import { isScriptAd } from "../../types/advert";
import useDeviceType from "../../hooks/useDeviceType";

/**
 * Props for the TabAdvert component.
 * @typedef {Object} TabAdvertProps
 * @property {number|Animated.Value} tabBarAdBottomAmount - The amount of bottom padding for the tab bar,
 *                                                          can be an animated value for smooth transitions.
 */
type TabAdvertProps = {
  tabBarAdBottomAmount: number | Animated.Value;
};

/**
 * A component that displays a sticky advertisement banner at the bottom of the screen.
 * The ad is positioned above the tab bar with proper spacing.
 * The component tracks ad impressions and clicks for analytics.
 *
 * @param {TabAdvertProps} props - Component props
 * @returns {JSX.Element|null} The tab advertisement component or null if ad shouldn't be shown
 */
const TabAdvert = ({ tabBarAdBottomAmount }: TabAdvertProps) => {
  // Get application dimensions for proper sizing
  const [appHeightAndWidth] = useAtom(appHeightAndWidthAtom);
  // Get available advertisements from global state
  const [adverts] = useAtom(advertisingAtom);
  // Track the layout dimensions of the tab ad
  const setTabAdLayout = useSetAtom(tabAdLayoutAtom);
  // Store the rendered advertisement element
  const [tabAdvert, setTabAdvert] = useState<JSX.Element | null>(null);

  // Hook to determine if the tab ad should be visible based on app state
  const { shouldShowTabAd } = useShowTabAd();

  const { deviceType } = useDeviceType();

  /**
   * Creates the tab advertisement element when adverts change.
   * Configures the ad banner with proper styling, click handlers,
   * and tracks ad impressions.
   */
  useMemo(() => {
    // Clear the tab ad if there are no tab adverts available
    if (!!adverts && !adverts?.tab) {
      setTabAdvert(null);
      return;
    }

    if (!adverts?.tab) return;

    // Create the tab advertisement component
    const tabAd = (
      <AdBanner
        accessibilityLabel="Banner Advert"
        onPress={() => {
          // Log analytics event when ad is clicked
          logEvent({ key: "sticky-clicked", data: {} });
          // Handle ad click action
          advertClick(adverts.tab.link_url);
        }}
        style={[
          {
            aspectRatio: 8.14 / 1,
            backgroundColor: variables.colors.palette.rtv.primary,
            minHeight: appHeightAndWidth.appWidth / 8.14,
          },
          Platform.OS == "web"
            ? {
                justifyContent: isScriptAd(adverts.tab) ? "center" : "flex-end",
              }
            : {},
        ]}
        ad={adverts.tab}
        tab
      />
    );

    setTabAdvert(tabAd);
  }, [adverts?.tab?.campaign_id]);

  // Don't render anything if tab ads shouldn't be shown
  // and zero the tab ad dimensions (for other components)
  useEffect(() => {
    if (!shouldShowTabAd || !tabAdvert) {
      setTabAdLayout({ height: 0, width: 0, x: 0, y: 0 });
    }
  }, [shouldShowTabAd, tabAdvert]);

  if (!shouldShowTabAd || !tabAdvert) {
    return null;
  }

  return (
    <Animated.View
      style={{
        marginBottom: tabBarAdBottomAmount,

        ...(Platform.OS === "web" && deviceType === "PHONE"
          ? {
              overflow: "hidden",
              position: "absolute",
              bottom: 0,
            }
          : {}),
      }}
      onLayout={(event) => {
        // Update the tab ad layout dimensions when rendered
        if (tabAdvert) setTabAdLayout(event.nativeEvent.layout);
      }}
    >
      {tabAdvert}
    </Animated.View>
  );
};

export default TabAdvert;
